/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { createContext, ReactElement, ReactNode, useContext, useRef } from "react"
import BookingUiStore, { BookingView } from "./stores/booking-ui-store"
import BookingSessionStore from "./stores/booking-session-store"
import WizardStore from "./stores/wizard-store"

type BookingStores = {
  uiStore: BookingUiStore
  sessionStore: BookingSessionStore
  wizardStore: WizardStore
}

const BookingContext = createContext<BookingStores | null>(null)

const BookingContextProvider = ({
  initialView,
  children,
}: {
  initialView: BookingView
  children: ReactNode
}): ReactElement => {
  const storesRef = useRef<BookingStores | null>(null)
  if (!storesRef.current) {
    const uiStore = new BookingUiStore(initialView)
    storesRef.current = {
      uiStore,
      sessionStore: new BookingSessionStore(uiStore),
      wizardStore: new WizardStore(),
    }
  }
  return <BookingContext.Provider value={storesRef.current}>{children}</BookingContext.Provider>
}

const useBookingStores = (): BookingStores => {
  const stores = useContext(BookingContext)
  if (!stores) {
    throw new Error("useBookingStores must be used within a BookingContextProvider")
  }
  return stores
}

export { BookingContextProvider, useBookingStores }
